import isEqual from "lodash.isequal";

import { type TReportMode } from "../../../../redux/slices/entriesGeneratorSlice";
import type { ICol, ISection } from "../../../../interfaces";

export const reportModeData: Array<{ label: string; value: TReportMode }> = [
  { label: "All", value: "all" },
  { label: "Entries", value: "entries" },
  { label: "Checklists", value: "checkLists" },
  { label: "Attachments", value: "attachments" },
  { label: "Entries & Checklists", value: "entriesAndCheckLists" },
  { label: "Entries & Attachments", value: "entriesAndAttachments" },
];
/**
 * Checks if two columns are equal based on their properties.
 *
 * @param col1 - The first column to compare.
 * @param col2 - The second column to compare.
 * @returns A boolean indicating whether the two columns are equal.
 */
const isColEqual = (col1: ICol, col2: ICol) => {
  if (col1.unit === "steering.pumps" && col2.unit === "steering.pumps") {
    return true;
  }

  if (col1.name === col2.name && col1.unit === col2.unit) {
    if (col1.unit === "sub" && col1.content && col2.content) {
      return isEqual(col1.content, col2.content);
    }

    // Compare values
    if (typeof col1.value === typeof col2.value) {
      if (typeof col1.value === "object") {
        return isEqual(col1.value, col2.value);
      } else if (typeof col1.value === "boolean") {
        return col1.value === col2.value;
      } else if (col1.value && col2.value) {
        return String(col1.value) === String(col2.value);
      } else {
        return !col1.value && !col2.value;
      }
    }
  }
  return false;
};
type THistoryRow = { cols: ICol[] } | ICol[];

interface DeletedRowData {
  aid: string;
  yachtid: string;
  lbid: string;
  rid: string;
  reason: string;
  data: {
    sectionIndex: number;
    deletedRowIndex: number;
    rows: ICol[][];
    [key: string]: any;
  };
  userid: string;
  date: string;
}

/**
 * Normalizes a history row to ICol[] format, handling both direct arrays and wrapped objects.
 *
 * @param row - The row to normalize (either ICol[] or { cols: ICol[] }).
 * @returns The normalized ICol[] array.
 */
const normalizeHistoryRow = (row: ICol[] | { cols: ICol[] }): ICol[] => {
  return Array.isArray(row) ? row : row.cols;
};

/**
 * Checks if two rows are equal by comparing their columns.
 *
 * @param row1 - The first row to compare.
 * @param row2 - The second row to compare.
 * @returns A boolean indicating whether the two rows are equal.
 */
const isRowEqual = (row1: THistoryRow, row2: THistoryRow) => {
  if (!row1 || !row2) {
    return false;
  }

  const r1 = "cols" in row1 ? [...row1.cols] : [...row1];
  const r2 = "cols" in row2 ? [...row2.cols] : [...row2];

  if (r1.length !== r2.length) {
    return false;
  }

  for (let i = 0; i < r1.length; i++) {
    if (!isColEqual(r1[i], r2[i])) {
      return false;
    }
  }

  return true;
};
/**
 * Checks if a row is empty by evaluating its columns.
 *
 * @param row - The row to check.
 * @returns A boolean indicating whether the row is empty.
 */
const isRowEmpty = (row: THistoryRow) => {
  if (!row) {
    return true;
  }

  const cols = "cols" in row ? [...row.cols] : [...row];

  for (const col of cols) {
    if (col.value) {
      if (col.unit === "24lock") {
        return !col.value.time || col.value.time === ":";
      }
      if (col.unit === "time") {
        return !col.value || col.value === ":";
      }
      return false;
    }
  }

  return true;
};
/**
 *
 * Processes history rows by filtering and removing duplicates or empty rows.
 *
 * @param historyRows - An array of history rows to process.
 * @param row - The current section to compare against.
 * @returns An array of processed history rows.
 */
const handleHistoryRows = (historyRows: Array<ICol[]>, row: THistoryRow) => {
  let hrows: any = [];

  if (historyRows.length > 0) {
    hrows.push(historyRows[0]);
    if (historyRows.length > 1) {
      for (let i = 1; i < historyRows.length; i++) {
        if (!isRowEqual(historyRows[i], historyRows[i - 1])) {
          hrows.push(historyRows[i]);
        }
      }
    }

    hrows = [...hrows.filter((row: any) => !isRowEmpty(row))];

    if (isRowEqual(hrows[hrows.length - 1], row)) {
      hrows.pop();
    }
  }

  return hrows;
};

/**
 * Maps section history rows and deleted rows to a structured format.
 *
 * @param sectionHistory - An array of section history rows (for changed/edited rows)
 * @param section - The current section containing rows to compare against
 * @param deletedRows - Array of deleted rows for this section
 * @returns A two-dimensional array of mapped history rows including deleted rows
 */
export const mapHistoryRows = (
  history: Array<ICol[][]> | Array<Array<{ cols: ICol[] }>> | undefined,
  section: ISection | undefined,
  deletedRows: DeletedRowData[] = [],
): ICol[][][] => {
  if (!section || !section.rows) {
    return [];
  }

  const sectionHistory = history || [];
  const currentRowCount = section.rows.length;

  // Get deleted rows for this section and sort them by their original indices
  const sortedDeletedRows = deletedRows
    .filter(dr => dr.data && dr.data.rows && dr.data.rows.length > 0)
    .sort((a, b) => a.data.deletedRowIndex - b.data.deletedRowIndex);

  // Result structure: [current rows with history] + [deleted rows]
  const result: ICol[][][] = [];

  // Step 1: Process current rows (with their history above them)
  for (let rowIndex = 0; rowIndex < currentRowCount; rowIndex++) {
    const currentRow = section.rows[rowIndex];
    // Handle the case where rows might be ICol[] directly or IRow with cols property
    const currentRowCols = Array.isArray(currentRow)
      ? currentRow
      : currentRow.cols
        ? Object.values(currentRow.cols)
        : [];
    const rowHistory: ICol[][] = [];

    // Deduplicate consecutive identical history entries
    let previousHistoryRow: ICol[] | null = null;

    // Process history in reverse order (newest to oldest) to maintain chronological deduplication
    for (
      let historyIndex = sectionHistory.length - 1;
      historyIndex >= 0;
      historyIndex--
    ) {
      const historySection = sectionHistory[historyIndex];

      if (Array.isArray(historySection) && historySection.length > rowIndex) {
        const historyRow = historySection[rowIndex]; // This is ICol[] or {cols: ICol[]}
        const historyRowCols = normalizeHistoryRow(historyRow); // Normalize to ICol[]

        // Skip if this history row is identical to the previous history row
        if (
          previousHistoryRow &&
          isRowEqual(historyRowCols, previousHistoryRow)
        ) {
          continue; // Skip this duplicate
        }

        // Compare history with current row - only add if different
        const isHistoryDifferent = !isRowEqual(historyRowCols, currentRowCols);

        if (isHistoryDifferent) {
          rowHistory.unshift(historyRowCols); // Add to beginning to maintain chronological order
        }

        previousHistoryRow = historyRowCols; // Update for next comparison
      }
    }

    // Add history rows first, then current row - but only if there's actually history or we need to show the current row
    const rowData: ICol[][] = [];

    // Add history (strikethrough) above current row - only if there are actual changes
    if (rowHistory.length > 0) {
      rowHistory.forEach(historyRow => {
        rowData.push(historyRow);
      });
    }

    // Add current row (normal)
    rowData.push(currentRowCols);

    result.push(rowData);
  }

  // Step 2: Add deleted rows after all current rows (all strikethrough)
  sortedDeletedRows.forEach(deletedRowData => {
    const { rows } = deletedRowData.data;
    if (rows && rows.length > 0) {
      // Each deleted row is its own group
      result.push([rows[0]]);
    }
  });

  return result;
};
